import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, ButtonGroup } from 'reactstrap';
import Select from 'react-select';
import {
    reviewPrescription,
    reviewPrescriptionDocument,
} from '../../../services/api/cashless-vaccination';

const REJECTION_REASONS = [
    { value: 'Illegible / Blurry image', label: 'Illegible / Blurry image' },
    { value: 'Wrong document uploaded', label: 'Wrong document uploaded' },
    { value: 'Prescription expired', label: 'Prescription expired' },
    { value: 'Doctor details missing', label: 'Doctor details missing' },
    { value: 'Patient name mismatch', label: 'Patient name mismatch' },
];

const STATUS_BADGE_COLOR = { approved: 'success', rejected: 'danger', uploaded: 'warning' };
const DOC_STATUS_LABEL = { '1': 'Valid', '-1': 'Invalid', '0': 'Pending' };
const DOC_STATUS_COLOR = { '1': 'success', '-1': 'danger', '0': 'secondary' };

const PrescriptionReview = ({
    requestId,
    prescriptionStatus,
    prescriptionRejectionReason,
    prescriptionLinks,
    onRefresh,
}) => {
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [customReason, setCustomReason] = useState('');
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [docLoading, setDocLoading] = useState({});

    const handleApprove = () => {
        setApproving(true);
        reviewPrescription(requestId, 'approve')
            .then(() => {
                toast.success('Prescription approved successfully');
                onRefresh();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Failed to approve prescription';
                toast.error(msg);
            })
            .finally(() => setApproving(false));
    };

    const handleRejectSubmit = () => {
        const reasonValue = selectedReason?.value || customReason.trim();
        if (!reasonValue) {
            toast.error('Rejection reason is required');
            return;
        }
        setRejecting(true);
        reviewPrescription(requestId, 'reject', reasonValue)
            .then(() => {
                toast.success('Prescription rejected');
                setShowRejectForm(false);
                setSelectedReason(null);
                setCustomReason('');
                onRefresh();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Failed to reject prescription';
                toast.error(msg);
            })
            .finally(() => setRejecting(false));
    };

    const handleDocReview = (documentId, status) => {
        setDocLoading((prev) => ({ ...prev, [documentId]: true }));
        reviewPrescriptionDocument(documentId, status)
            .then(() => {
                toast.success(status === 1 ? 'Document marked as valid' : 'Document marked as invalid');
                onRefresh();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Failed to update document status';
                toast.error(msg);
            })
            .finally(() => setDocLoading((prev) => ({ ...prev, [documentId]: false })));
    };

    const badgeColor = STATUS_BADGE_COLOR[prescriptionStatus] || 'secondary';
    const links = Array.isArray(prescriptionLinks) ? prescriptionLinks : [];

    return (
        <div>
            <div className="mb-3">
                <strong>Prescription Status:</strong>{' '}
                <Badge color={badgeColor} pill>
                    {prescriptionStatus || 'None'}
                </Badge>
                {prescriptionStatus === 'rejected' && prescriptionRejectionReason && (
                    <div className="mt-1 small text-danger">
                        Rejection Reason: {prescriptionRejectionReason}
                    </div>
                )}
            </div>

            <div className="mb-3">
                <ButtonGroup>
                    <Button
                        color="success"
                        size="sm"
                        disabled={approving || prescriptionStatus === 'approved'}
                        onClick={handleApprove}
                    >
                        {approving ? 'Approving…' : 'Approve'}
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        className="ml-1"
                        disabled={rejecting || prescriptionStatus === 'rejected'}
                        onClick={() => {
                            setShowRejectForm((prev) => !prev);
                            setSelectedReason(null);
                            setCustomReason('');
                        }}
                    >
                        Reject
                    </Button>
                </ButtonGroup>
            </div>

            {showRejectForm && (
                <div className="mb-3" style={{ maxWidth: 420 }}>
                    <Select
                        options={REJECTION_REASONS}
                        value={selectedReason}
                        onChange={(reason) => {
                            setSelectedReason(reason);
                            if (reason) setCustomReason('');
                        }}
                        placeholder="Select rejection reason…"
                        isClearable
                    />
                    {!selectedReason && (
                        <input
                            type="text"
                            className="form-control mt-2"
                            placeholder="Or enter custom reason…"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                        />
                    )}
                    <div className="mt-2">
                        <Button
                            color="danger"
                            size="sm"
                            disabled={rejecting}
                            onClick={handleRejectSubmit}
                        >
                            {rejecting ? 'Rejecting…' : 'Confirm Rejection'}
                        </Button>
                        <Button
                            color="secondary"
                            size="sm"
                            className="ml-2"
                            onClick={() => {
                                setShowRejectForm(false);
                                setSelectedReason(null);
                                setCustomReason('');
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            <div className="mt-3">
                <strong>Prescription Documents</strong>
                {links.length > 0 ? (
                    <table className="table table-sm mt-2">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>File</th>
                                <th>Uploaded By</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {links.map((doc, idx) => {
                                const statusKey = String(doc.status);
                                const isLoading = !!docLoading[doc.id];
                                return (
                                    <tr key={doc.id}>
                                        <td>{idx + 1}</td>
                                        <td>
                                            <a
                                                href={doc.documentLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                View
                                            </a>
                                        </td>
                                        <td>{doc.uploadedBy || '—'}</td>
                                        <td>
                                            <Badge color={DOC_STATUS_COLOR[statusKey] || 'secondary'} pill>
                                                {DOC_STATUS_LABEL[statusKey] || 'Pending'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                color="success"
                                                size="sm"
                                                className="mr-1"
                                                disabled={isLoading || !doc.id || doc.status === 1}
                                                onClick={() => handleDocReview(doc.id, 1)}
                                            >
                                                Valid
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                disabled={isLoading || !doc.id || doc.status === -1}
                                                onClick={() => handleDocReview(doc.id, -1)}
                                            >
                                                Invalid
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-muted mt-2">No prescription documents found.</div>
                )}
            </div>
        </div>
    );
};

export default PrescriptionReview;
